/**
 * FmShellSlotsContext — route-scoped content for shell chrome slots.
 *
 * Pages can register scrubber/verdict chrome without reaching into the shell DOM.
 * The registration hook clears on unmount so slot content does not leak between
 * protected routes.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface FmShellSlotContent {
  /** Bottom glass scrubber content. null/undefined leaves the slot empty. */
  scrubber?: ReactNode;
  /** Floating verdict chip content. null/undefined leaves the slot empty. */
  verdict?: ReactNode;
  /** CSS length for --fm-scrubber-slot-h. Omit to keep default zero height. */
  scrubberHeight?: string;
  /** Optional mobile override for --fm-scrubber-slot-h-mobile. */
  scrubberMobileHeight?: string;
}

export interface FmShellSlotsContextValue {
  scrubber: ReactNode;
  verdict: ReactNode;
  scrubberHeight?: string;
  scrubberMobileHeight?: string;
  setSlots: (slots: FmShellSlotContent) => void;
  clearSlots: () => void;
}

type FmShellSlotsState = Pick<
  FmShellSlotsContextValue,
  'scrubber' | 'verdict' | 'scrubberHeight' | 'scrubberMobileHeight'
>;

const EMPTY_SLOTS: FmShellSlotsState = {
  scrubber: null,
  verdict: null,
};

const FmShellSlotsContext = createContext<FmShellSlotsContextValue | undefined>(undefined);

export const FmShellSlotsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [slots, setSlotState] = useState<FmShellSlotsState>(EMPTY_SLOTS);

  const setSlots = useCallback((nextSlots: FmShellSlotContent) => {
    setSlotState({
      scrubber: nextSlots.scrubber ?? null,
      verdict: nextSlots.verdict ?? null,
      scrubberHeight: nextSlots.scrubberHeight,
      scrubberMobileHeight: nextSlots.scrubberMobileHeight,
    });
  }, []);

  const clearSlots = useCallback(() => {
    setSlotState(EMPTY_SLOTS);
  }, []);

  const value = useMemo<FmShellSlotsContextValue>(
    () => ({
      ...slots,
      setSlots,
      clearSlots,
    }),
    [slots, setSlots, clearSlots],
  );

  return (
    <FmShellSlotsContext.Provider value={value}>
      {children}
    </FmShellSlotsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- standard context hook; matches existing context files
export const useFmShellSlots = (): FmShellSlotsContextValue => {
  const ctx = useContext(FmShellSlotsContext);
  if (!ctx) throw new Error('useFmShellSlots must be used within FmShellSlotsProvider');
  return ctx;
};

// eslint-disable-next-line react-refresh/only-export-components -- page-level hook API for route-scoped shell slot registration
export function useFmShellSlotRegistration(slots: FmShellSlotContent): void {
  const { setSlots, clearSlots } = useFmShellSlots();
  const { scrubber, verdict, scrubberHeight, scrubberMobileHeight } = slots;

  useLayoutEffect(() => {
    setSlots({ scrubber, verdict, scrubberHeight, scrubberMobileHeight });
  }, [setSlots, scrubber, verdict, scrubberHeight, scrubberMobileHeight]);

  useLayoutEffect(() => {
    return () => {
      clearSlots();
    };
  }, [clearSlots]);
}

export const FmShellSlotRegistration: React.FC<FmShellSlotContent> = (slots) => {
  useFmShellSlotRegistration(slots);
  return null;
};
